import { z } from 'zod';
import { VALID_CONTENT_TYPES } from './contentTypeMapping';

/**
 * Reusable validation schemas for forms throughout the site
 */

// Common field validators
export const emailSchema = z
  .string()
  .trim()
  .min(1, 'Email is required')
  .email('Please enter a valid email address')
  .max(255, 'Email must be less than 255 characters');

export const nameSchema = z
  .string()
  .trim()
  .min(1, 'Name is required')
  .max(100, 'Name must be less than 100 characters');

export const urlSchema = z
  .string()
  .trim()
  .url('Please enter a valid URL')
  .or(z.literal(''));

// Library Item validation
export const libraryItemSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, 'Title is required')
    .max(200, 'Title must be less than 200 characters'),
  description: z
    .string()
    .trim()
    .min(1, 'Description is required')
    .max(10000, 'Description must be less than 10,000 characters'),
  type: z.enum(VALID_CONTENT_TYPES, {
    errorMap: () => ({ message: 'Please select a valid content type' })
  }),
  content_url: z
    .string()
    .trim()
    .min(1, 'Content URL is required')
    .url('Please enter a valid URL'),
  meta_title: z
    .string()
    .trim()
    .max(60, 'Meta title must be 60 characters or less')
    .optional()
    .nullable(),
  meta_description: z
    .string()
    .trim()
    .max(160, 'Meta description must be 160 characters or less')
    .optional()
    .nullable(),
  og_title: z
    .string()
    .trim()
    .max(95, 'OG title must be 95 characters or less')
    .optional()
    .nullable(),
  og_description: z
    .string()
    .trim()
    .max(300, 'OG description must be 300 characters or less')
    .optional()
    .nullable(),
  phase: z.string().optional().nullable(),
  cause: z.string().optional().nullable(),
  audience_type: z.string().optional().nullable(),
  keywords: z.array(z.string()).optional(),
});

// Contact form validation
export const contactFormSchema = z.object({
  reason: z
    .string()
    .min(1, 'Please select a reason for contact'),
  firstName: nameSchema,
  lastName: nameSchema,
  email: emailSchema,
  organization: z
    .string()
    .trim()
    .min(1, 'Organization is required')
    .max(200, 'Organization name must be less than 200 characters'),
  message: z
    .string()
    .trim()
    .max(5000, 'Message must be less than 5,000 characters')
    .optional(),
});

// Event registration validation
export const eventRegistrationSchema = z.object({
  firstName: nameSchema,
  lastName: nameSchema,
  email: emailSchema,
  attendeeType: z.string().optional(),
  bringingChildren: z.enum(['yes', 'no', '']).optional(),
  childCount: z.string().optional(),
  childAges: z.string().max(200, 'Child ages must be less than 200 characters').optional(),
  dietaryRestrictions: z.string().max(500, 'Dietary restrictions must be less than 500 characters').optional(),
  hopeToGet: z.string().max(2000, 'Response must be less than 2,000 characters').optional(),
  personalizationOptIn: z.boolean().optional(),
});

// Livestream registration validation
export const livestreamRegistrationSchema = z.object({
  firstName: nameSchema,
  lastName: nameSchema,
  email: emailSchema,
  attendeeType: z.string().optional(),
  hopeToGet: z.string().max(2000, 'Response must be less than 2,000 characters').optional(),
  personalizationOptIn: z.boolean().optional(),
});

// Resource submission validation
export const resourceSubmissionSchema = z.object({
  textInput: z
    .string()
    .max(50000, 'Text input must be less than 50,000 characters'),
});

// Feature request validation
export const featureRequestSchema = z.object({
  name: z.string().trim().max(100, 'Name must be less than 100 characters').optional(),
  email: emailSchema.optional().or(z.literal('')),
  featureRequestText: z
    .string()
    .trim()
    .min(10, 'Please provide more detail about your feature request')
    .max(5000, 'Feature request must be less than 5,000 characters'),
  wantsUpdates: z.boolean().optional(),
});

/**
 * Validation result type for better type narrowing
 */
export type ValidationResult<T> = 
  | { success: true; data: T; errors?: never }
  | { success: false; errors: Record<string, string>; data?: never };

/**
 * Validate data against a schema and return formatted errors
 */
export function validateForm<T>(
  schema: z.ZodSchema<T>,
  data: unknown
): ValidationResult<T> {
  const result = schema.safeParse(data);
  
  if (result.success) {
    return { success: true, data: result.data };
  }
  
  const errors: Record<string, string> = {};
  for (const issue of result.error.issues) {
    const path = issue.path.join('.');
    if (!errors[path]) {
      errors[path] = issue.message;
    }
  }
  
  return { success: false, errors };
}

/**
 * Get character count status for a field
 */
export function getCharacterStatus(
  value: string | undefined | null,
  maxLength: number
): { count: number; remaining: number; status: 'ok' | 'warning' | 'error' } {
  const count = value?.length || 0;
  const remaining = maxLength - count;
  
  let status: 'ok' | 'warning' | 'error' = 'ok';
  if (remaining < 0) {
    status = 'error';
  } else if (remaining < maxLength * 0.15) {
    status = 'warning';
  }
  
  return { count, remaining, status };
}
